import { ImageName } from "@mcleod/images";
import { CommonDialogs } from "@mcleod/common";
import { ClickEvent, DataDisplayEvent, DataSourceMode, LabelProps, Layout, Overlay, OverlayedList, Toast } from "@mcleod/components";
import { ActionPermission, Alignment, Api, LogManager, ModelRow, PermissionsUtil, StringUtil } from "@mcleod/core";
import { DataFusionUtil, SendLoadTender } from "@mcleod/datafusion";
import { LMEGeneralSettings } from "@mcleod/general";
import { BrokerageTrackingActions, InitiateBrokerageTracking, ModelBrokerageTracking, SendRateConfirmation } from "@mcleod/powerbroker";
import { UnassignCarrierPrompt } from "./UnassignCarrierPrompt";
import { AutogenLayoutAssignedMovementActions } from "./autogen/AutogenLayoutAssignedMovementActions";

const log = LogManager.getLogger("lme/datafusion/AssignedMovementActions");

export class AssignedMovementActions extends AutogenLayoutAssignedMovementActions {
    private _doAfterAction: () => void = () => this.mainDataSource.search(this.mainDataSource.lastSearch);
    trackingOverlayedList: OverlayedList;
    tenderOverlayedList: OverlayedList;
    slideoutProps = {
        width: 1200, height: window.innerHeight, backgroundColor: "defaultBackground", top: 30, padding: 0,
        borderRadius: 4, borderWidth: 1, borderShadow: true, borderColor: "strokeSecondary"
    };

    override onLoad() {
        this.buttonLoadTracking.addMouseEnterListener(() => this.showOrderTrackingBorder(true));
        this.buttonLoadTracking.addMouseLeaveListener(() => {
            if (this.trackingOverlayedList == null)
                this.showOrderTrackingBorder(false)
        });
        this.buttonSendDataFusionTender.visible = false;
        this.buttonSendRateCon.visible = false;
    }

    public get doAfterAction(): () => void {
        return this._doAfterAction;
    }

    public set doAfterAction(value: () => void) {
        this._doAfterAction = value;
    }

    private executeDoAfterAction() {
        if (this.doAfterAction != null)
            this.doAfterAction();
    }

    /** This is an event handler for the onClick event of buttonSendRateCon.  */
    buttonSendRateConOnClick(event: ClickEvent) {
        SendRateConfirmation.showSendRateConfirmationSlideout(this.buttonSendRateCon, this.movement.get("id"), this._doAfterAction, this.slideoutProps);
    }

    /** This is an event handler for the onClick event of buttonInitiateTracking.  */
    buttonInitiateTrackingOnClick(event: ClickEvent) {
        this.initiateOrEditTracking(event);
    }

    private initiateOrEditTracking(clickEvent: ClickEvent, editTracking?: boolean) {
        const moveId = this.movement?.get("id");
        const orderId = this.mainDataSource.activeRow?.get("orders.id");

        if (moveId != null) {
            Layout.getLayout("lme/powerbroker/InitiateBrokerageTracking", { ...this.slideoutProps }).addLayoutLoadListener(event => {
                const layout = event.target as InitiateBrokerageTracking;
                layout.editTracking = editTracking;
                layout.buttonInitiateLater.visible = false;
                layout.buttonCancel.addClickListener(() => layout.slideOut());
                this.buttonInitiateTracking.busy = true;
                layout.search({ "id": moveId, "orders.id": orderId }).then(result => {
                    if (result?.modelRows?.length > 0) {
                        layout.setAllDataSourcesToMode(DataSourceMode.UPDATE)
                        layout.slideIn({ direction: Alignment.RIGHT }, true, { closeOnClickOff: false, greyedBackground: true });
                        layout.doAfterTrackingInitiated = () => {
                            layout.slideOut();
                            if (layout.mainDataSource.activeRow?.get("br_vendor") != null) {
                                this.executeDoAfterAction();
                            }
                        }
                    }
                }).finally(() => this.buttonInitiateTracking.busy = false);
            });
        }
    }

    private cancelTracking() {
        const moveId = this.movement?.get("id");
        CommonDialogs.showYesNo(`Movement ${moveId} will no longer be tracked by ${this.sourceMovement.activeRow?.get("br_vendor_name", "")}. Would you like to continue?`,
            "Cancel Tracking", { titleProps: { imageName: ImageName.circleX, imageColor: "primary.light" } }).then(yes => {
                if (yes) {
                    this.buttonLoadTracking.busy = true;
                    Api.post("lme/powerbroker/tracking/cancel-tracking", { "movement_ids": [moveId] }).then(response => {
                        Toast.showSuccessToast(`Tracking Canceled for Movement ${moveId} `);
                        this.executeDoAfterAction();
                    }).finally(() => this.buttonLoadTracking.busy = false);
                }
            })
    }

    /** This is an event handler for the onClick event of buttonOrderTracking.  */
    buttonLoadTrackingOnClick(event: ClickEvent) {
        const dropDownItems: Partial<LabelProps>[] = [
            { text: "Cancel Tracking", onClick: () => this.cancelTracking() }
        ];
        if (this.canUpdateTracking())
            dropDownItems.push({ text: "Edit Tracking", onClick: () => this.initiateOrEditTracking(event, true) })
        if (this.canSendTrackingMsg())
            dropDownItems.push({
                text: "Send Message",
                onClick: () => new BrokerageTrackingActions()
                    .sendTrackingMessage(
                        this.movement.get("id"),
                        this.movement.get("br_vendor_name", this.movement.get("br_vendor")),
                        this.sendMessageOnClose,
                        this.sendMessageDoAfterMessageSent)
            });

        this.trackingOverlayedList = Overlay.showDropdown(this.buttonLoadTracking, dropDownItems, null, { width: this.buttonLoadTracking.width }, {
            onClose: () => {
                this.trackingOverlayedList = null;
                this.showOrderTrackingBorder(false);
            }
        })
    }

    canSendTrackingMsg(): boolean {
        const vendor = this.movement?.get("br_vendor");
        if (vendor == null) return false;
        switch (vendor) {
            case ModelBrokerageTracking.MACROPOINT_LITE_VENDOR:
            case ModelBrokerageTracking.LOAD_TRACK_VENDOR:
            case ModelBrokerageTracking.FOURKITES_VENDOR:
                return true;
            default: return false;
        }
    }

    canUpdateTracking(): boolean {
        const vendor = this.movement?.get("br_vendor");
        if (vendor == null) return false;
        switch (vendor) {
            case ModelBrokerageTracking.MACROPOINT_LITE_VENDOR:
            case ModelBrokerageTracking.LOAD_TRACK_VENDOR:
            case ModelBrokerageTracking.PROJECT44_VENDOR:
                return true;
            default: return false;
        }
    }

    private showOrderTrackingBorder(value: boolean) {
        this.buttonLoadTracking._element.style.outline = value ? "1px solid #cdcdcd" : null;
    }

    public async removeCarrier(moveRow: ModelRow = this.activeRow, tonu_earning_code?: String) {
        AssignedMovementActions.unassignCarrier(moveRow, tonu_earning_code, this.doAfterAction);
    }

    public static async unassignCarrier(moveRow: ModelRow, tonu_earning_code?: String, doAfterAction?: () => void) {
        const moveId = moveRow?.get("id");

        if (moveId != null) {
            const permDenied = moveRow?.get("status", null) === "D" && PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_DEL_MOVES);
            if (permDenied) {
                CommonDialogs.showDialog("You do not have permission to remove carriers from delivered movements.");
                return;
            }

            const dispatchControlSettings = LMEGeneralSettings.getSingleton().getDispatchControl();
            if (dispatchControlSettings.enforce_revenu_cod == 'Y' && moveRow?.get("orders.revenue_code_id") == null) {
                CommonDialogs.showError("A revenue code is required for the order.");
                return;
            }

            if (dispatchControlSettings.enforce_billto == 'Y' && moveRow?.get("orders.customer_id") == null) {
                CommonDialogs.showError("A Bill-to customer is required for the order.");
                return;
            }

            const carrierId = moveRow.get("override_payee_id");
            const brTrackingVendor = moveRow?.get("br_vendor");
            UnassignCarrierPrompt.showPrompt(moveRow, tonu_earning_code, async (success: boolean, vendorName: string, forceCancel: boolean, unassignLayout: UnassignCarrierPrompt) => {
                    DataFusionUtil.isCancelAllowed(carrierId, moveId).then(cancelAllowed => {
                        if (cancelAllowed) {
                            DataFusionUtil.sendCancelTender(carrierId, moveId, true);
                        } else {
                            unassignLayout.toastMessage = `${moveRow.get("payee.name")} was removed from movement ${moveId}.`;
                            if (brTrackingVendor) {
                                if (forceCancel)
                                    unassignLayout.toastMessage += ' Tracking removed by forced cancellation.';
                                else
                                unassignLayout.toastMessage += ' ' + vendorName + ' tracking canceled.';
                            }
                        }
                    });
                    await new Promise(resolve => setTimeout(resolve, 2500)).then(() => doAfterAction());
            });
        }
    }

    /** This is an event handler for the onDisplay event of sourceMovement.  */
    async sourceMovementOnDisplay(event: DataDisplayEvent) {
        const panelVisible = this.movement != null;
        if (panelVisible) {
            const activeVendorExist = this.movement.getBoolean("is_active_br_vendor", false);
            if (activeVendorExist) {
                const showOrderTracking = !StringUtil.isEmptyString(this.movement.get("br_vendor"));
                this.buttonInitiateTracking.visible = !showOrderTracking;
                this.buttonLoadTracking.visible = showOrderTracking;
            } else {
                this.buttonInitiateTracking.visible = false;
                this.buttonLoadTracking.visible = false;
            }
        }
    }


    private get movement(): ModelRow<any> {
        return this.mainDataSource?.activeRow;
    }

    private sendMessageOnClose(overLay: Overlay): void {
        Overlay.hideOverlay(overLay);
    }

    private sendMessageDoAfterMessageSent(overlay: Overlay, response: any): void {
        Overlay.hideOverlay(overlay);
        const message = response?.data?.[0]?.toast_message;
        if (message) {
            Toast.showSuccessToast(message);
        }
    }

    public setMenuOptions(payeeId: string) {
        const carrierId = payeeId;

        if (carrierId != null) {
            this.checkCarrierForDataFusion(carrierId).then(result => {
                if (result == "false") {
                    this.buttonSendRateCon.visible = true;
                    this.buttonSendDataFusionTender.visible = false;
                }
                else {
                    this.buttonSendRateCon.visible = false;
                    this.buttonSendDataFusionTender.visible = true;
                }
            });
        }
    }

    async checkCarrierForDataFusion(payeeId: String): Promise<string> {
        let result = "";
        await Api.search("lme/datafusion/df-carrier-search", {
            payee_id: payeeId
        }).then(response => {
            const data = response.data[0];
            result = data.is_datafusion_carrier;
        });
        return result;
    }

    async buttonSendLoadTenderOnClick(event: ClickEvent) {
        const moveId = this.movement?.get("id");
        const carrierId = this.movement?.get("override_payee_id");

        if (moveId != null) {
            await DataFusionUtil.isCancelAllowed(carrierId, moveId).then(result => {
                const cancelAllowed: boolean = result;

                const extraItems: Partial<LabelProps>[] = [
                    { text: "Send DataFusion Tender", onClick: () => this.sendLoadTender(carrierId, moveId, "false") }
                ];

                if (cancelAllowed) {
                    extraItems.push({ text: "Send DataFusion Cancel", onClick: () => this.sendLoadTender(carrierId, moveId, "true") });
                }
                extraItems.push({ text: "Send Rate Confirmation", onClick: () => this.sendRC() });

                this.tenderOverlayedList = Overlay.showDropdown(this.buttonSendDataFusionTender, extraItems, null, { width: this.buttonSendDataFusionTender.width },
                    {
                        onClose: () => {
                            this.tenderOverlayedList = null;
                        }
                    });
            });
        }
    }

    sendLoadTender(carrierId: string, moveId: string, isCancel: string) {
        SendLoadTender.sendLoadTender(carrierId, moveId, isCancel, () => {
            this.refresh();
        });
    }

    sendRC() {
        const moveId = this.movement?.get("id");
        const orderId = this.mainDataSource.activeRow?.get("orders.id");
        if (moveId != null) {
            Layout.getLayout("lme/powerbroker/SendRateConfirmation", { ...this.slideoutProps, width: 1000 }).addLayoutLoadListener(event => {
                const layout = event.target as SendRateConfirmation;
                layout.panelRateConData.paddingTop = 25;
                layout.buttonCancel.addClickListener(() => layout.slideOut());
                this.buttonSendRateCon.busy = true;
                layout.search({ movement_id: moveId }).then(result => {
                    if (result?.modelRows?.length > 0) {
                        layout.doAfterRateConSent = (() => layout.slideOut().then(() => {
                            this.executeDoAfterAction();
                        }));
                        layout.setAllDataSourcesToMode(DataSourceMode.UPDATE);
                        layout.slideIn({ direction: Alignment.RIGHT }, true, { closeOnClickOff: false, greyedBackground: true });
                    }
                }).finally(() => this.buttonSendRateCon.busy = false);
            });
        }
    }

    refresh() {
        log.debug("Refreshing after tender sent.")
        this.mainDataSource.search(this.mainDataSource.lastSearch);
    }
}
